/**
 * Managed AI access mode + assignment panel.
 *
 * Reused in both workspace:ai and workspace:automations settings tabs (DRY).
 */
import { SettingRow } from '@affine/component/setting-components';
import { GraphQLService } from '@affine/core/modules/cloud/services/graphql';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { type FC, useCallback, useEffect, useState } from 'react';

import { BudgetLimitsPanel } from './budget-limits-panel';

export const BudgetLimitsToggleRow: FC<{ isAdmin: boolean }> = ({
  isAdmin,
}) => {
  const t = useI18n();
  const graphqlService = useService(GraphQLService);
  const workspaceId = useService(WorkspaceService).workspace.id;
  const [mode, setMode] = useState<'all_members' | 'selected_assignments'>(
    'all_members'
  );

  useEffect(() => {
    let cancelled = false;
    graphqlService
      .gql({
        query: {
          id: 'budgetLimitsSettingsQuery',
          query: `query budgetLimitsSettings($id: String!) { workspace(id: $id) { managedAIAccessMode enableBudgetLimits } }`,
          operationName: 'budgetLimitsSettings',
          definitionName: 'workspace',
        } as any,
        variables: { id: workspaceId } as any,
      })
      .then((res: any) => {
        if (!cancelled) {
          setMode(
            res?.workspace?.managedAIAccessMode === 'selected_assignments' ||
              (!res?.workspace?.managedAIAccessMode &&
                res?.workspace?.enableBudgetLimits)
              ? 'selected_assignments'
              : 'all_members'
          );
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [workspaceId, graphqlService]);

  const handleModeChange = useCallback(
    (nextMode: 'all_members' | 'selected_assignments') => {
      if (!isAdmin) return;
      const previousMode = mode;
      setMode(nextMode);
      graphqlService
        .gql({
          query: {
            id: 'updateWorkspaceBudgetLimitsToggle',
            query: `mutation updateWorkspace($input: UpdateWorkspaceInput!) {
              updateWorkspace(input: $input) { id managedAIAccessMode enableBudgetLimits }
            }`,
            operationName: 'updateWorkspace',
            definitionName: 'updateWorkspace',
          } as any,
          variables: {
            input: {
              id: workspaceId,
              managedAIAccessMode: nextMode,
              enableBudgetLimits: nextMode === 'selected_assignments',
            },
          } as any,
        })
        .catch(() => setMode(previousMode));
    },
    [workspaceId, graphqlService, isAdmin, mode]
  );

  return (
    <>
      <SettingRow
        name={t['com.streamlain.settings.workspace.ai.budget-limits']()}
        desc={t['com.streamlain.settings.workspace.ai.budget-limits-desc']()}
        style={{ marginBottom: 10 }}
      >
        <select
          data-testid="managed-ai-access-mode"
          value={mode}
          onChange={event =>
            handleModeChange(
              event.target.value as 'all_members' | 'selected_assignments'
            )
          }
          disabled={!isAdmin}
        >
          <option value="all_members">
            {t['com.streamlain.settings.workspace.ai.access.all-members']()}
          </option>
          <option value="selected_assignments">
            {t[
              'com.streamlain.settings.workspace.ai.access.selected-assignments'
            ]()}
          </option>
        </select>
      </SettingRow>
      {mode === 'selected_assignments' && (
        <div data-testid="budget-limits-panel" style={{ marginBottom: 16 }}>
          <BudgetLimitsPanel isAdmin={isAdmin} />
        </div>
      )}
    </>
  );
};
